import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * Featured stories data for the welcome section
 */
const stories = [
  {
    title: "The Earth's Wonder",
    description:
      "The journey into geological engineering begins with curiosity about Earth's materials and processes. From analyzing rock formations to studying mineral resources, every discovery helps us understand the dynamic forces that shape our planet. Our program provides hands-on experience, allowing students to explore the fascinating world of geology through fieldwork and research.",
    image: "https://www.mtu.edu/geo/what-is-geological/images/geological-square500.jpg",
    accent: "from-emerald-400 to-teal-600"
  },
  {
    title: "Building a Sustainable Future",
    description:
      "Geological engineers play a vital role in sustainable development. Whether it's assessing natural hazards, designing environmentally friendly mining operations, or ensuring safe infrastructure, our students gain the knowledge and skills to make a lasting impact. Through real-world applications and innovative problem-solving, we prepare future engineers to address global challenges.",
    image: "https://www.bgs.ac.uk/wp-content/uploads/2022/03/Chan-Sow-Lin-MRT.jpg",
    accent: "from-blue-400 to-indigo-600"
  },
  {
    title: "Engineering for a Resilient Planet",
    description:
      "Natural disasters like earthquakes, landslides, and floods require expert analysis and mitigation strategies. Geological engineers use advanced technology to assess risks and develop solutions that protect communities from environmental hazards while ensuring long-term sustainability.",
    image: "https://newsghana.com.gh/wp-content/uploads/2022/08/WhatsApp-Image-at-PM-9.jpeg",
      accent: "from-blue-400 to-indigo-600"
  },
];

/**
 * StoryItem component displays an individual story with premium styling
 */
const StoryItem = ({ item, index }) => {
  const { title, description, image, accent } = item;
  const isEven = index % 2 === 0;
  const itemRef = useRef(null);

  useEffect(() => {
    if (!itemRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.2 }
    );
    
    observer.observe(itemRef.current);
    
    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={itemRef}
      className={`flex flex-col ${
        isEven ? "md:flex-row" : "md:flex-row-reverse"
      } gap-0 mb-32 md:mb-40 items-center opacity-0 translate-y-8 transition-all duration-700 ease-out`}
      style={{ transformStyle: "preserve-3d" }}
    >
      {/* Image Section with Decorative Elements */}
      <div className="w-full md:w-1/2 flex justify-center relative">
        <div className="relative z-10 w-full max-w-lg">
          <div className={`absolute -inset-0.5 bg-gradient-to-r ${accent} rounded-xl blur opacity-50 group-hover:opacity-100 transition duration-1000`}></div>
          <img
            src={image}
            alt={title}
            className="relative z-20 w-full h-auto rounded-xl shadow-2xl object-cover transition-all duration-500 hover:scale-105 hover:shadow-lg hover:shadow-black/30"
            style={{ maxHeight: "450px" }}
          />
          <div className={`absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-gradient-to-br ${accent} z-10 opacity-90`}></div>
          <div className="absolute -top-4 -left-4 w-16 h-16 rounded-full bg-white z-10 opacity-30 backdrop-blur-lg"></div>
        </div>
      </div>

      {/* Content Section with Premium Styling */}
      <div className="w-full md:w-1/2 px-6 md:px-12 py-8 relative">
        <div className={`h-1 w-24 bg-gradient-to-r ${accent} rounded-full mb-6`}></div>
        <h3 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
          <span className={`bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>
            {title}
          </span>
        </h3>
        <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6 backdrop-blur-sm">
          {description}
        </p>
      </div>
    </div>
  );
};

StoryItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    accent: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * WelcomeMessage component with premium styling and animations
 */
export const WelcomeMessage = () => {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 md:py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-100 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-100 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-radial from-gray-50 to-transparent opacity-80"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10">
        <div className="text-center mb-20 md:mb-32">
          <div className="inline-block mb-2">
            <span className="inline-block px-3 py-1 bg-gradient-to-r from-teal-500 to-blue-500 text-white text-xs font-semibold rounded-full uppercase tracking-wider">
              Geological Engineering
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-600 to-indigo-600">
              Exploring The Earth
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover how our program combines science, innovation, and engineering to 
            create sustainable solutions for our planet's greatest challenges.
          </p>
          <div className="w-32 h-1 bg-gradient-to-r from-teal-400 to-blue-500 mx-auto mt-8 rounded-full"></div>
        </div>

        <div className="space-y-16 md:space-y-28">
          {stories.map((item, index) => (
            <StoryItem key={index} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Add custom CSS class for animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .animate-in {
      opacity: 1 !important;
      transform: translateY(0) !important;
    }

    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }

    @keyframes pulse {
      0% { opacity: 0.6; }
      50% { opacity: 1; }
      100% { opacity: 0.6; }
    }
  `;
  document.head.appendChild(style);
}

export default WelcomeMessage;